import { supabase } from '../utils/supabase';

export interface SystemStatus {
  status: 'online' | 'degraded' | 'offline';
  supabase: ServiceStatus;
  internet: ServiceStatus;
  lastChecked: Date;
}

export interface ServiceStatus {
  status: 'operational' | 'degraded' | 'down';
  latency?: number;
  error?: string;
  lastChecked: Date;
}

export class HealthCheckService {
  private static readonly SUPABASE_TIMEOUT = 5000;
  private static readonly INTERNET_TIMEOUT = 3000;

  static async checkSupabaseHealth(): Promise<ServiceStatus> {
    const startTime = Date.now();

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.SUPABASE_TIMEOUT);

      const { error } = await supabase
        .from('organizations')
        .select('id')
        .limit(1)
        .abortSignal(controller.signal);

      clearTimeout(timeoutId);

      if (error) {
        return {
          status: 'down',
          error: error.message,
          lastChecked: new Date(),
        };
      }

      const latency = Date.now() - startTime;

      return {
        status: latency > 2000 ? 'degraded' : 'operational',
        latency,
        lastChecked: new Date(),
      };
    } catch (error) {
      return {
        status: 'down',
        error: error instanceof Error ? error.message : 'Unknown error',
        lastChecked: new Date(),
      };
    }
  }

  static async checkInternetConnectivity(): Promise<ServiceStatus> {
    const startTime = Date.now();

    if (!navigator.onLine) {
      return {
        status: 'down',
        error: 'No internet connection detected',
        lastChecked: new Date(),
      };
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.INTERNET_TIMEOUT);

      const response = await fetch('https://httpbin.org/status/200', {
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const latency = Date.now() - startTime;

      if (response.ok) {
        return {
          status: latency > 1500 ? 'degraded' : 'operational',
          latency,
          lastChecked: new Date(),
        };
      } else {
        return {
          status: 'down',
          error: `HTTP ${response.status}`,
          lastChecked: new Date(),
        };
      }
    } catch (error) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.INTERNET_TIMEOUT);

        const fallbackResponse = await fetch('https://www.google.com/favicon.ico', {
          method: 'HEAD',
          cache: 'no-cache',
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        const latency = Date.now() - startTime;

        if (fallbackResponse.ok) {
          return {
            status: 'degraded',
            latency,
            lastChecked: new Date(),
          };
        }
      } catch {
        // Fallback also failed
      }

      return {
        status: 'down',
        error: error instanceof Error ? error.message : 'Network error',
        lastChecked: new Date(),
      };
    }
  }

  static async checkSystemHealth(): Promise<SystemStatus> {
    const [supabaseHealth, internetHealth] = await Promise.all([
      this.checkSupabaseHealth(),
      this.checkInternetConnectivity(),
    ]);

    let overallStatus: 'online' | 'degraded' | 'offline' = 'online';

    if (supabaseHealth.status === 'down' || internetHealth.status === 'down') {
      overallStatus = 'offline';
    } else if (supabaseHealth.status === 'degraded' || internetHealth.status === 'degraded') {
      overallStatus = 'degraded';
    }

    return {
      status: overallStatus,
      supabase: supabaseHealth,
      internet: internetHealth,
      lastChecked: new Date(),
    };
  }
}
